import { Injectable } from '@angular/core';

export interface ImageDimensions {
  width: number;
  height: number;
  isPortrait: boolean;
}

export interface FrameBounds {
  width: number;
  height: number;
}

export interface ImageTransformState {
  rotation: number;
  positionX: number;
  positionY: number;
  maxWidth: number;
  maxHeight: number;
}

export interface TextStyle {
  color: string;
  fontFamily: string;
  fontSize: number;
}

export interface TextOverlayState {
  text: string;
  positionX: number;
  positionY: number;
  textRotation?: number;
  style: TextStyle;
}

@Injectable({
  providedIn: 'root'
})
export class ImageEditorService {

  /**
   * Get image dimensions from a file
   */
  getImageDimensions(file: File): Promise<ImageDimensions> {
    return new Promise((resolve) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        const dimensions: ImageDimensions = {
          width: img.width,
          height: img.height,
          isPortrait: img.height > img.width
        };
        URL.revokeObjectURL(objectUrl);
        resolve(dimensions);
      };

      img.src = objectUrl;
    });
  }

  /**
   * Calculate initial image scale based on frame and image dimensions
   */
  calculateInitialScale(
    imageWidth: number,
    imageHeight: number,
    frameBounds: FrameBounds,
    isPortraitImage: boolean,
    displayMode: 'fill' | 'fit' = 'fill'
  ): { maxWidth: number; maxHeight: number; aspectRatio: number } {
    const aspectRatio = imageWidth / imageHeight;
    const frameAspectRatio = frameBounds.width / frameBounds.height;

    let maxWidth: number;
    let maxHeight: number;

    if (displayMode === 'fill') {
      if (isPortraitImage) {
        maxHeight = frameBounds.height * 2;
        maxWidth = maxHeight * aspectRatio;

        if (maxWidth < frameBounds.width) {
          maxWidth = frameBounds.width * 2;
          maxHeight = maxWidth / aspectRatio;
        }
      } else {
        maxWidth = frameBounds.width * 2;
        maxHeight = maxWidth / aspectRatio;

        if (maxHeight < frameBounds.height) {
          maxHeight = frameBounds.height * 2;
          maxWidth = maxHeight * aspectRatio;
        }
      }
    } else {
      if (aspectRatio > frameAspectRatio) {
        maxWidth = frameBounds.width * 2;
        maxHeight = maxWidth / aspectRatio;
      } else {
        maxHeight = frameBounds.height * 2;
        maxWidth = maxHeight * aspectRatio;
      }
    }

    return { maxWidth, maxHeight, aspectRatio };
  }

  /**
   * Apply dimension constraints while maintaining aspect ratio
   */
  applyDimensionConstraints(
    newWidth: number,
    newHeight: number,
    minWidth: number,
    minHeight: number,
    maxWidth: number,
    maxHeight: number,
    aspectRatio: number
  ): { width: number; height: number } {
    let limitReached = false;
    let finalWidth = newWidth;
    let finalHeight = newHeight;

    // Handle minimum size constraints
    if (newWidth < minWidth && newHeight < minHeight) {
      const widthRatio = minWidth / newWidth;
      const heightRatio = minHeight / newHeight;

      if (widthRatio > heightRatio) {
        finalWidth = minWidth;
        finalHeight = minWidth / aspectRatio;
      } else {
        finalHeight = minHeight;
        finalWidth = minHeight * aspectRatio;
      }
      limitReached = true;
    }
    // Handle maximum size constraints
    else if (newWidth > maxWidth && newHeight > maxHeight) {
      const widthRatio = newWidth / maxWidth;
      const heightRatio = newHeight / maxHeight;

      if (widthRatio > heightRatio) {
        finalWidth = maxWidth;
        finalHeight = maxWidth / aspectRatio;
      } else {
        finalHeight = maxHeight;
        finalWidth = maxHeight * aspectRatio;
      }
      limitReached = true;
    }
    // Apply the new dimensions if within limits
    else if (!limitReached) {
      finalWidth = newWidth;
      finalHeight = newHeight;
    }

    return { width: finalWidth, height: finalHeight };
  }

  /**
   * Constrain image position within frame bounds
   */
  constrainPosition(
    positionX: number,
    positionY: number,
    imageMaxWidth: number,
    imageMaxHeight: number,
    imageRotation: number,
    naturalWidth: number,
    naturalHeight: number,
    frameBounds: FrameBounds
  ): { positionX: number; positionY: number } {
    const imgRatio = naturalWidth / naturalHeight;

    let displayWidth: number;
    let displayHeight: number;

    if (imageMaxWidth / imageMaxHeight > imgRatio) {
      displayHeight = Math.min(naturalHeight, imageMaxHeight);
      displayWidth = displayHeight * imgRatio;
    } else {
      displayWidth = Math.min(naturalWidth, imageMaxWidth);
      displayHeight = displayWidth / imgRatio;
    }

    // Adjust for rotation if needed
    let effectiveWidth = displayWidth;
    let effectiveHeight = displayHeight;

    if (imageRotation % 180 !== 0) {
      [effectiveWidth, effectiveHeight] = [effectiveHeight, effectiveWidth];
    }

    // Calculate the maximum allowed position offsets
    const maxX = Math.max(0, (effectiveWidth - frameBounds.width) / 2);
    const maxY = Math.max(0, (effectiveHeight - frameBounds.height) / 2);

    // Constrain the position within the allowed range
    const constrainedX = Math.max(-maxX, Math.min(maxX, positionX));
    const constrainedY = Math.max(-maxY, Math.min(maxY, positionY));

    return { positionX: constrainedX, positionY: constrainedY };
  }

  /**
   * Calculate smart initial position for tall portrait images
   */
  calculateSmartInitialPosition(
    aspectRatio: number,
    displayMode: 'fill' | 'fit',
    imageMaxHeight: number,
    frameBounds: FrameBounds,
    naturalHeight: number
  ): number {
    if (displayMode === 'fill' && aspectRatio < 0.5) {
      const displayHeight = Math.min(naturalHeight, imageMaxHeight);
      const verticalOverflow = (displayHeight - frameBounds.height) / 2;

      if (verticalOverflow > 0) {
        return -verticalOverflow * 0.7; // Show more of the top portion
      }
    }
    return 0;
  }

  /**
   * Load image from source
   */
  loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  }

  /**
   * Create canvas for image rendering
   */
  createCanvas(frame: HTMLElement, isPortraitImage: boolean): HTMLCanvasElement | null {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { alpha: true });
    const SCALE_FACTOR = 5; // High resolution scale factor

    if (!ctx) return null;

    // Set dimensions based on orientation
    if (isPortraitImage) {
      canvas.width = frame.offsetHeight * SCALE_FACTOR;
      canvas.height = frame.offsetWidth * SCALE_FACTOR;
      ctx.translate(canvas.width, 0);
      ctx.rotate(90 * Math.PI / 180);
    } else {
      canvas.width = frame.offsetWidth * SCALE_FACTOR;
      canvas.height = frame.offsetHeight * SCALE_FACTOR;
    }

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    return canvas;
  }

  /**
   * Draw image and text on canvas
   */
  drawOnCanvas(
    canvas: HTMLCanvasElement,
    img: HTMLImageElement,
    transformState: ImageTransformState,
    textState: TextOverlayState | null,
    isPortraitImage: boolean
  ): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const SCALE_FACTOR = 5;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();

    // Calculate center point
    const centerX = isPortraitImage ? canvas.height / 2 : canvas.width / 2;
    const centerY = isPortraitImage ? canvas.width / 2 : canvas.height / 2;

    // Apply transformations
    ctx.translate(
      centerX + (transformState.positionX * SCALE_FACTOR),
      centerY + (transformState.positionY * SCALE_FACTOR)
    );
    ctx.rotate((transformState.rotation * Math.PI) / 180);

    // Calculate and apply image scale
    const scale = Math.min(
      (transformState.maxWidth * SCALE_FACTOR) / img.width,
      (transformState.maxHeight * SCALE_FACTOR) / img.height
    );

    // Draw image
    ctx.drawImage(
      img,
      -(img.width * scale) / 2,
      -(img.height * scale) / 2,
      img.width * scale,
      img.height * scale
    );

    // Restore context for text
    ctx.restore();

    // Draw text if present
    if (textState && textState.text) {
      ctx.save();

      const scaledFontSize = textState.style.fontSize * SCALE_FACTOR;
      ctx.font = `900 ${scaledFontSize}px ${textState.style.fontFamily}`;
      ctx.fillStyle = textState.style.color;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';

      // Convert percentage position into actual canvas position
      const canvasW = isPortraitImage ? canvas.height : canvas.width;
      const canvasH = isPortraitImage ? canvas.width : canvas.height;
      const textX = (textState.positionX / 100) * canvasW;
      const textY = (textState.positionY / 100) * canvasH;

      // Shadow for white text
      if (textState.style.color === '#FFFFFF') {
        ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
        ctx.shadowBlur = 4 * SCALE_FACTOR;
        ctx.shadowOffsetX = 2 * SCALE_FACTOR;
        ctx.shadowOffsetY = 2 * SCALE_FACTOR;
      }

      // Apply rotation around the text anchor point (matching HTML transform: translate(-50%,-50%) rotate())
      if (textState.textRotation) {
        ctx.translate(textX, textY);
        ctx.rotate((textState.textRotation * Math.PI) / 180);
        ctx.translate(-textX, -textY);
      }

      // Calculate text metrics to center it properly (matching HTML transform: translate(-50%, -50%))
      const lines = textState.text.split('\n');
      const lineHeight = scaledFontSize * 1.01;
      const totalHeight = lineHeight * lines.length;
      
      // Find the widest line for horizontal centering
      let maxLineWidth = 0;
      lines.forEach(line => {
        const metrics = ctx.measureText(line);
        maxLineWidth = Math.max(maxLineWidth, metrics.width);
      });

      // Draw each line, centering both horizontally and vertically
      lines.forEach((line, i) => {
        const lineY = textY - totalHeight / 2 + i * lineHeight + lineHeight / 2;
        const lineX = textX - maxLineWidth / 2; // Center horizontally
        ctx.fillText(line, lineX, lineY);
      });

      ctx.restore();
    }

    // Apply final overlay
    ctx.fillStyle = 'rgba(255, 242, 0, 0.02)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }

  /**
   * Generate PDF from canvas
   */
  async generatePDF(canvas: HTMLCanvasElement): Promise<Blob> {
    const { default: jsPDF } = await import('jspdf');

    // Credit card dimensions in mm (85.5 x 54)
    const creditCardWidth = 86;
    const creditCardHeight = 54.3180091;

    // Create PDF with credit card dimensions
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: [creditCardWidth, creditCardHeight]
    });

    // Calculate positioning to center the image
    const imageAspectRatio = canvas.width / canvas.height;
    const pdfAspectRatio = creditCardWidth / creditCardHeight;

    let finalWidth = creditCardWidth;
    let finalHeight = creditCardHeight;

    // Adjust dimensions to maintain aspect ratio while filling the space
    if (imageAspectRatio > pdfAspectRatio) {
      finalHeight = creditCardWidth / imageAspectRatio;
    } else {
      finalWidth = creditCardHeight * imageAspectRatio;
    }

    // Calculate centering offsets
    const xOffset = (creditCardWidth - finalWidth) / 2;
    const yOffset = (creditCardHeight - finalHeight) / 2;

    // Add the image to PDF with high quality
    pdf.addImage(
      canvas.toDataURL('image/png', 1),
      'PNG',
      xOffset,
      yOffset,
      finalWidth,
      finalHeight,
      undefined,
      'FAST'
    );

    // Return the PDF as blob
    return pdf.output('blob');
  }

  /**
   * Capture edited image to base64 and PDF
   */
  async captureEditedImage(
    frame: HTMLElement,
    imageSrc: string,
    transformState: ImageTransformState,
    textState: TextOverlayState | null,
    isPortraitImage: boolean
  ): Promise<{ base64Image: string; pdfBlob: Blob }> {
    const canvas = this.createCanvas(frame, isPortraitImage);

    if (!canvas) {
      throw new Error('Could not create canvas');
    }

    // Load and draw image
    const img = await this.loadImage(imageSrc);
    this.drawOnCanvas(canvas, img, transformState, textState, isPortraitImage);

    // Get high-res base64
    const base64Image = canvas.toDataURL('image/jpeg', 1.0);

    // Generate PDF
    const pdfBlob = await this.generatePDF(canvas);

    return { base64Image, pdfBlob };
  }
}

